import React, {useState, useCallback, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {
  submitInvoiceRegistration,
  resetInvoiceRegistration,
} from '../../actions/invoiceRegistration.action';
import Loading from '../../components/Loading/Loading';
import {
  EMAIL_REGEX,
  PUBLIC_INVOICE_REG_STEP_LABELS,
} from '../../constants/constants';
import {PRICE_COMPARISON_PATH} from '../../constants/routePaths';
import {
  PageWrapper,
  PageTitle,
  StepIndicatorWrapper,
  StepItem,
  StepDot,
  StepLabel,
  StepLine,
  StepCard,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  FieldError,
  ErrorMessage,
  ButtonRow,
  PrimaryButton,
  LoadingOverlay,
  ConfirmationWrapper,
  ConfirmationTitle,
  ConfirmationText,
} from './InvoiceRegistration.style';

const StepIndicator = ({currentStep}) => (
  <StepIndicatorWrapper>
    {PUBLIC_INVOICE_REG_STEP_LABELS.map((label, i) => (
      <React.Fragment key={label}>
        <StepItem>
          <StepDot $active={currentStep === i} $done={currentStep > i}>
            {currentStep > i ? '✓' : i + 1}
          </StepDot>
          <StepLabel $active={currentStep === i}>{label}</StepLabel>
        </StepItem>
        {i < PUBLIC_INVOICE_REG_STEP_LABELS.length - 1 && (
          <StepLine $done={currentStep > i} />
        )}
      </React.Fragment>
    ))}
  </StepIndicatorWrapper>
);

StepIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
};

const InvoiceRegistration = ({match, history}) => {
  const dispatch = useDispatch();
  const invoiceScanningId = match.params.invoiceId;
  const auth = useSelector(state => state.auth);
  const registration = useSelector(state => state.invoiceRegistration || {});
  const {
    submitting,
    success,
    error: contactError,
    needsNewUpload,
  } = registration;

  // The signup endpoint ignores rather than rejects a token (see
  // self-serve-signup.pdf), so a logged-in visitor landing here (a stale
  // link, or logging in via the header modal without leaving this page)
  // would otherwise be able to submit a pointless duplicate application —
  // they already have an account.
  useEffect(() => {
    if (auth) {
      history.replace('/');
    }
  }, [auth, history]);

  // Start from a clean slate — this is global state, so a previous
  // attempt's success/error must not leak into a fresh visit.
  useEffect(() => {
    dispatch(resetInvoiceRegistration());
  }, [dispatch]);

  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [confirmEmailError, setConfirmEmailError] = useState(null);
  const [mobileError, setMobileError] = useState(null);

  useEffect(() => {
    if (success) {
      setCurrentStep(1);
    }
  }, [success]);

  const handleContactNext = useCallback(() => {
    setEmailError(null);
    setConfirmEmailError(null);
    setMobileError(null);

    const emailVal = email.trim();
    const confirmVal = confirmEmail.trim();
    const mobileVal = mobile.trim();

    if (!emailVal) {
      setEmailError('Please enter the customer email address.');
      return;
    }
    if (!EMAIL_REGEX.test(emailVal)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!confirmVal) {
      setConfirmEmailError('Please confirm the customer email address.');
      return;
    }
    if (emailVal !== confirmVal) {
      setConfirmEmailError('Email addresses do not match.');
      return;
    }
    if (!mobileVal) {
      setMobileError('Please enter the customer mobile number.');
      return;
    }

    dispatch(submitInvoiceRegistration(invoiceScanningId, emailVal, mobileVal));
  }, [dispatch, email, confirmEmail, mobile, invoiceScanningId]);

  const exploreProducts = () => history.push('/');

  const renderContactStep = () => (
    <StepCard>
      {submitting && (
        <LoadingOverlay>
          <Loading isLight={false} />
          <p>Saving…</p>
        </LoadingOverlay>
      )}
      <SectionTitle>Contact information</SectionTitle>
      {contactError && (
        <ErrorMessage>
          {contactError}
          {needsNewUpload && (
            <>
              {' '}
              <Link to={PRICE_COMPARISON_PATH}>Start a new upload</Link>.
            </>
          )}
        </ErrorMessage>
      )}

      <FormGroup>
        <Label htmlFor="registration-email">
          Customer Email <span>(Obligatory)</span>
        </Label>
        <Input
          id="registration-email"
          type="email"
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setEmailError(null);
            setConfirmEmailError(null);
          }}
          placeholder="email@example.com"
          $hasError={!!emailError}
          disabled={submitting}
        />
        {emailError && <FieldError>{emailError}</FieldError>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="registration-confirm-email">
          Confirm Customer Email <span>(Obligatory)</span>
        </Label>
        <Input
          id="registration-confirm-email"
          type="email"
          value={confirmEmail}
          onChange={e => {
            setConfirmEmail(e.target.value);
            setConfirmEmailError(null);
          }}
          placeholder="Please confirm the email"
          $hasError={!!confirmEmailError}
          disabled={submitting}
          onPaste={e => e.preventDefault()}
        />
        {confirmEmailError && <FieldError>{confirmEmailError}</FieldError>}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="registration-mobile">
          Customer Mobile <span>(Obligatory)</span>
        </Label>
        <Input
          id="registration-mobile"
          type="tel"
          value={mobile}
          onChange={e => {
            setMobile(e.target.value);
            setMobileError(null);
          }}
          placeholder="+44 7700 000000"
          $hasError={!!mobileError}
          disabled={submitting}
        />
        {mobileError && <FieldError>{mobileError}</FieldError>}
      </FormGroup>

      <ButtonRow>
        <PrimaryButton onClick={handleContactNext} disabled={submitting}>
          Next
        </PrimaryButton>
      </ButtonRow>
    </StepCard>
  );

  const renderConfirmationStep = () => (
    <ConfirmationWrapper>
      <ConfirmationTitle>We&apos;re Reviewing Your Details</ConfirmationTitle>
      <ConfirmationText>
        Thanks for signing up! We&apos;re reviewing your details now &mdash;
        this is usually pretty quick, but can take up to an hour.
      </ConfirmationText>
      <ConfirmationText>
        Once approved, you&apos;ll get a confirmation email at{' '}
        <strong>{email}</strong>.
      </ConfirmationText>
      <ConfirmationText>
        While you wait, why not browse our products?
      </ConfirmationText>
      <PrimaryButton onClick={exploreProducts}>
        Explore our products
      </PrimaryButton>
    </ConfirmationWrapper>
  );

  if (auth) {
    return null;
  }

  return (
    <PageWrapper>
      <PageTitle>New Customer Registration</PageTitle>
      <StepIndicator currentStep={currentStep} />
      {currentStep === 0 && renderContactStep()}
      {currentStep === 1 && renderConfirmationStep()}
    </PageWrapper>
  );
};

InvoiceRegistration.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default InvoiceRegistration;
